/**
 * offlineQueue.js — Offline action queue for web client
 *
 * When the user performs mutations (like, comment, friend request) while
 * offline, they are queued in localStorage and replayed when connectivity
 * returns.
 *
 * Architecture:
 *  - Queue stored in localStorage as JSON array
 *  - Each entry has: { id, method, url, data, timestamp }
 *  - On reconnect, entries are replayed in FIFO order
 *  - Failed replays are retried up to 3 times, then discarded
 */

const QUEUE_KEY = 'connects_offline_queue';

/**
 * Get the current queue from localStorage.
 * @returns {Array}
 */
function getQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Save the queue to localStorage.
 * @param {Array} queue
 */
function saveQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/**
 * Enqueue an action for later execution.
 *
 * @param {string} method - HTTP method (POST, PUT, DELETE)
 * @param {string} url - API endpoint
 * @param {object|null} data - Request body
 * @returns {string} - Queue entry ID
 */
export function enqueueAction(method, url, data = null) {
  const queue = getQueue();
  const entry = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    method: method.toUpperCase(),
    url,
    data,
    timestamp: new Date().toISOString(),
    retries: 0,
  };
  queue.push(entry);
  saveQueue(queue);
  console.log(`[OfflineQueue] Queued ${method} ${url} (${queue.length} pending)`);
  return entry.id;
}

/**
 * Replay all queued actions in order.
 * Called when connectivity is restored.
 *
 * @param {import('axios').AxiosInstance} api - Axios instance to use for replaying
 * @returns {Promise<{ success: number, failed: number }>}
 */
export async function replayQueue(api) {
  const queue = getQueue();
  if (queue.length === 0) return { success: 0, failed: 0 };

  console.log(`[OfflineQueue] Replaying ${queue.length} queued actions...`);

  let success = 0;
  let failed = 0;
  const remaining = [];

  for (const entry of queue) {
    try {
      await api({
        method: entry.method,
        url: entry.url,
        data: entry.data,
      });
      success++;
      console.log(`[OfflineQueue] ✅ Replayed ${entry.method} ${entry.url}`);
    } catch (error) {
      entry.retries = (entry.retries || 0) + 1;
      if (entry.retries < 3) {
        remaining.push(entry);
        console.warn(`[OfflineQueue] ⚠️ Retry ${entry.retries}/3 for ${entry.url}`);
      } else {
        failed++;
        console.error(`[OfflineQueue] ❌ Discarded after 3 retries: ${entry.url}`);
      }
    }
  }

  saveQueue(remaining);
  console.log(`[OfflineQueue] Done: ${success} success, ${failed} failed, ${remaining.length} pending retry`);
  return { success, failed };
}

/**
 * Get the number of pending actions.
 * @returns {number}
 */
export function pendingCount() {
  return getQueue().length;
}

/**
 * Clear all pending actions.
 */
export function clearQueue() {
  saveQueue([]);
}

/**
 * Install online/offline event listeners to auto-replay.
 *
 * @param {import('axios').AxiosInstance} api
 */
export function installOfflineSync(api) {
  window.addEventListener('online', () => {
    console.log('[OfflineQueue] Connection restored — replaying queue...');
    replayQueue(api);
  });

  window.addEventListener('offline', () => {
    console.log('[OfflineQueue] Connection lost — actions will be queued');
  });
}
